/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, ReactElement, useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import Device from '@ion/core/src/services/Device';
import {AssetType, Devices} from '@ion/core/src/types/entities';
import {padDelimiterLeft} from '@ion/core/src/utils/StringUtil';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {isSideSheetCollapsedView} from '@ion/dls/src/components/sidesheet/helpers';
import {AssetTypeIcon} from '@ion/dls/src/icons/AssetTypeIcon';
import {MapEditIcon} from '@ion/dls/src/icons/MapEditIcon';
import {NotificationUrgentIcon} from '@ion/dls/src/icons/NotificationUrgentIcon';
import {ScDeviceIcon} from '@ion/dls/src/icons/ScDeviceIcon';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsColor, dlsLayout, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme, SideSheetView} from '@ion/dls/src/types/types';
import type {AssetCardProps} from './AssetCard';

export interface LegacyAssetCardContentProps {
  asset: Device;
  sideSheetView?: SideSheetView;
  assetDetailView?: boolean;
  toolConnected?: boolean;
  detailOnClick?: (e: MouseEvent<HTMLSpanElement>, asset: Devices) => void;
  onDeviceEdit?: Function;
  renderList?: (props: any) => ReactElement;
  cardProps: AssetCardProps;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    dls: {
      motion: {
        element: {
          largeExpansion: {
            close: closeLargeExpansion,
            open: openLargeExpansion
          }
        }
      }
    }
  } = theme;
  return {
    left : {
      display        : 'flex',
      justifyContent : 'space-between'
    },
    right : {
      display    : 'flex',
      alignItems : 'center'
    },
    cursor : {
      cursor : 'pointer'
    },
    warning : {
      marginLeft : dlsLayout('spacing')
    },
    alias : {
      ...dlsTypography('panelCards'),
      color          : dlsColor('grey2'),
      justifyContent : 'space-between'
    },
    user : {
      ...dlsTypography('panelCards'),
      marginBottom   : dlsLayout('spacing') + 4,
      justifyContent : 'space-between'
    },
    detailCta : {
      display       : 'flex',
      fontSize      : '12px',
      textTransform : 'none',
      letterSpacing : 'normal',
      '&:hover' : { // eslint-disable-line key-spacing
        textDecoration : 'underline',
        '& svg' : { // eslint-disable-line key-spacing
          transform  : 'translateX(3px)',
          transition : 'transform 250ms ease-in-out'
        }
      }
    },
    detailChevron : {
      transition : 'transform 250ms ease-in-out',
      fontSize   : '23px'
    },
    items : {
      display : 'inline-flex'
    },
    author : {
      fontFamily    : 'Roboto',
      textTransform : 'uppercase',
      fontWeight    : '400',
      fontSize      : '12px',
      lineHeight    : '17px',
      color         : dlsColor('grey2')
    },
    driver : {
      fontFamily    : 'Roboto',
      textTransform : 'uppercase',
      fontWeight    : '600',
      fontSize      : '12px',
      lineHeight    : '17px',
      letterSpacing : 0,
      color         : dlsColor('aaBlue')
    },
    listWrapper : {
      maxHeight  : '1000px',
      transition : `max-height ${closeLargeExpansion}`,
      overflow   : 'hidden',
      marginTop  : 8
    },
    listWrapperListView : {
      maxHeight  : '0px',
      transition : `max-height ${openLargeExpansion}`,
      overflow   : 'hidden'
    },
    clickableTitle : {
      fontFamily : 'Roboto',
      fontWeight : '600',
      fontSize   : '16px',
      lineHeight : '19px',
      color      : dlsColor('grey2'),
      '&:hover'  : {
        textDecoration : 'underline'
      }
    },
    itemPointerEvents : {
      pointerEvents : 'auto'
    },
    assetTitle : {
      fontFamily : 'Roboto',
      fontWeight : '600',
      fontSize   : '16px',
      lineHeight : '19px',
      color      : dlsColor('grey2')
    },
    assetTitles : {
      display       : 'flex',
      height        : '100%',
      flexDirection : 'column',
      alignItems    : 'flex-start'
    },
    row : {
      display    : 'flex',
      alignItems : 'center'
    },
    icons : {
      display   : 'flex',
      alignSelf : 'center',
      height    : '100%'
    }
  };
});

/**
 * if asset.getAssetName === undefined || null, this device/ asset is not
 * linked SC devices ...
 */
const getDisplayedName = (asset: Device) => {
  const assetName = asset?.getAssetName();
  if (!assetName) {
    return asset?.getItemValue({
      deviceAttribute : 'deviceName',
      cvsAttribute    : '',
      isCVSAttribute  : false
    });
  };
  return assetName;
};

/**
 * LegacyAssetCardContent renders `AssetCard`'s legacy-theme markup - the
 * asset title/author/driver row, the type/SC-device icon, and the
 * expandable `renderList` content below it. Split out from `AssetCard` so
 * the legacy and current-theme (`AssetSummaryCard`) render paths don't
 * share one file/component.
 *
 * ```typescript
 *
 *   import {LegacyAssetCardContent} from '@/routes/tracking/LegacyAssetCardContent';
 *
 *   <LegacyAssetCardContent asset={asset}
 *     sideSheetView={sideSheetView}
 *     cardProps={props} />
 *
 * ```
 *
 */
export const LegacyAssetCardContent = ({
  asset,
  sideSheetView,
  assetDetailView,
  toolConnected = true,
  detailOnClick,
  onDeviceEdit,
  renderList,
  cardProps
}: LegacyAssetCardContentProps) => {
  const classes             = useStyles();
  const isSupplyChainDevice = asset?.isScDevice();
  const {
    assetType,
    operatorNames
  } = asset?.getDevice();
  const driver = padDelimiterLeft(operatorNames);
  const shouldCenterTitle = assetDetailView || sideSheetView === 'collapsed';

  const detailView = useCallback(function(event) {
    event.stopPropagation();
    if (!assetDetailView && detailOnClick) {
      detailOnClick(event, asset);
    }
  }, [asset, assetDetailView, detailOnClick]);

  const onEdit = useCallback(function() {
    onDeviceEdit?.();
  }, [onDeviceEdit]);

  return (
    <>
      <div className={classes.left}>
        <div className={classnames(classes.right, classes.cursor, classes.itemPointerEvents)}
          onClick={detailView}>
          <div className={classes.assetTitles} style={{justifyContent: shouldCenterTitle ? 'center' : 'normal'}}>
            <div className={classes.row}>
              <div className={!assetDetailView && detailOnClick ? classes.clickableTitle : classes.assetTitle}>
                {getDisplayedName(asset)}
              </div>
              {!toolConnected &&
              <NotificationUrgentIcon color={dlsColor('caRed')}
                size={18}
                style={{padding: '0 6px'}} />}
            </div>
            <span className={classes.author}>
              <ConditionalRender condition={!isSupplyChainDevice}>
                {asset?.getItemValue({deviceAttribute: 'deviceIdValue', cvsAttribute: 'assetAlias', isCVSAttribute: true})}
              </ConditionalRender>
              <ConditionalRender condition={isSupplyChainDevice}>
                {asset?.getItemValue({deviceAttribute: 'assetAlias', cvsAttribute: 'assetAlias', isCVSAttribute: false})}
              </ConditionalRender>
            </span>
            <span className={classes.driver}>
              {driver}
            </span>
          </div>
        </div>
        <div className={classes.icons}>
          <ConditionalRender condition={!isSupplyChainDevice}>
            <AssetTypeIcon assetType={AssetType[assetType || 'Vehicle']}
              color={assetType === AssetType.Tool ? dlsColor('grey1') : dlsColor('grey4')}
              size={34} />
          </ConditionalRender>
          <ConditionalRender condition={isSupplyChainDevice}>
            <>
              {!assetDetailView && <ScDeviceIcon color={dlsColor('grey4')} size={32}/>}
              {assetDetailView && <IconButton onClick={onEdit} icon={<MapEditIcon />} disableHover />}
            </>
          </ConditionalRender>
        </div>
      </div>
      {renderList && (
        <div className={isSideSheetCollapsedView(sideSheetView)
          ? classes.listWrapperListView
          : classes.listWrapper}>
          {renderList?.(cardProps)}
        </div>
      )}
    </>
  );
};

export default LegacyAssetCardContent;
